<template>
<div class="login-view">
    <div class="login-container">
        <div class="logo-container">
            <img :src="require('../assets/img/cuelistBLACK.png')" alt="Cuelist Logo" class="logo"/>
        </div>

        <div class="login-box">
            <div class="email-login">
                <h2 class="email-login-title" v-if="signingIn">Sign in with email</h2>
                <h2 class="email-login-title" v-else>Register with email</h2>
                
                <login-form 
                    v-if="signingIn" 
                    ref="loginForm"
                    @toggle-register="toggleRegister" 
                    @sso-login="login"
                    @attempt="signInError = ''"
                />
                <register-form v-else @toggle-register="toggleRegister" @success="createUser" />
            </div>

            <div class="divider">
                <span>or</span>
            </div>

            <div class="sso-login" v-if="signingIn">
                <md-button 
                    class="md-raised md-primary google-button" 
                    @click="login"
                    :disabled="loading">
                    <img src="https://www.gstatic.com/firebasejs/ui/2.0.0/images/auth/google.svg" alt="Google" class="google-icon" />
                    <span>Continue with Google</span>
                </md-button>
                <p class="signin-error" role="alert" v-if="signInError">{{ signInError }}</p>
            </div>

            <div class="sso-login" v-else>
                <h2>Register with Google</h2>
                <md-button 
                    class="md-raised md-primary google-button" 
                    @click="login"
                    :disabled="loading">
                    <img src="https://www.gstatic.com/firebasejs/ui/2.0.0/images/auth/google.svg" alt="Google" class="google-icon" />
                    <span>Continue with Google</span>
                </md-button>
                <p class="signin-error" role="alert" v-if="signInError">{{ signInError }}</p>
            </div>
        </div>

        <div class="terms-container">
            <p class="terms-text">
                By registering for and/or signing into Cuelist you signify your acceptance of both our 
                <a href="https://thecuelist.com/terms-of-use" target="_blank">Terms of Use</a> and 
                <a href="https://thecuelist.com/privacy" target="_blank">Privacy Policy</a>.
            </p>
        </div>
    </div>

    <core-footer/>

    <md-dialog-confirm
        :md-title="confirm.title"
        :md-content-html="getConfirmContent()"
        :md-ok-text="confirm.ok"
        :md-cancel-text="confirm.cancel"
        @close="onDialogClose"
        ref="loginSSODialog">
    </md-dialog-confirm>
</div>
</template>

<script>
import firebase from 'firebase/app';
import { db } from '@/main';
import UAParser from 'ua-parser-js';
import CoreFooter from '@/components/core/footer';
import AuthLoginForm from '@/components/auth/login-form';
import AuthRegisterForm from '@/components/auth/register-form';
import { consentFields } from '@/lib/marketingConsent';
import signinErrorMessage from '@/lib/signin-error-message';
import { googleNameFields } from '@/lib/user-name';

const LoginView = {
    name: 'LoginView',
    components: {
        'core-footer': CoreFooter,
        'login-form': AuthLoginForm,
        'register-form': AuthRegisterForm
    },
    props: ['redirect'],
    data() {
        return {
            signingIn: true,
            sessionID: '',
            deviceInfo: null,
            sessions: {},
            userRef: null,
            confirm: {
                title: 'Device Limit Reached',
                ok: 'Yes! Continue',
                cancel: 'Cancel'
            },
            loading: false,
            signInError: '',
            userProjectIds: []
        };
    },
    methods: {
        getConfirmContent() {
            return 'You are currently logged in on 3 devices. Signing in here will log you out from another device. Do you want to continue?';
        },
        getDeviceType(uaDeviceType) {
            if (uaDeviceType === 'mobile') return 'Mobile';
            if (uaDeviceType === 'tablet') return 'Tablet';
            return 'Desktop';
        },
        async getDeviceInfo() {
            const parser = new UAParser();
            const browserInfo = parser.getBrowser();
            const osInfo = parser.getOS();
            const device = parser.getDevice();
            const deviceType = this.getDeviceType(device.type);
            const deviceVendorModel = device.vendor ? `${device.vendor} (${device.model})` : '';

            let ip = 'Unknown';

            try {
                const ipResponse = await fetch('https://1.1.1.1/cdn-cgi/trace');
                ip = (await ipResponse.text()).match(/ip=([\d.]+)/)[1];
            } catch (error) {
                console.error('Failed to fetch IP:', error);
            }
            const loginTime = new Date().toISOString();

            return {
                deviceType,
                deviceVendorModel,
                browser: browserInfo.name && browserInfo.version ? `${browserInfo.name} (${browserInfo.version})` : browserInfo.name || 'Unknown',
                os: osInfo.name && osInfo.version ? `${osInfo.name} (${osInfo.version})` : osInfo.name || 'Unknown',
                ip,
                loginTime
            };
        },
        async login() {
            try {
                // One message at a time: a new try clears the email form's line too.
                this.signInError = '';
                if (this.$refs.loginForm) this.$refs.loginForm.errorCode = '';
                this.$store.dispatch('setLoaderLogin', { type: true });
                this.loading = true;
                const provider = new firebase.auth.GoogleAuthProvider();
                const res = await firebase.auth().signInWithPopup(provider);
                const user = res.user;
                user.signInMethod = 'google';

                // Google's own first/last name, when it has both (Sun 25).
                const googleNames = googleNameFields(res.additionalUserInfo && res.additionalUserInfo.profile);
                await this.createUser(user, undefined, googleNames || null);
                const array = new Uint8Array(32);
                window.crypto.getRandomValues(array);
                this.sessionID = Array.from(array, byte => byte.toString(16).padStart(2, '0')).join('');
                this.deviceInfo = await this.getDeviceInfo();
                this.userRef = db.collection('users').doc(user.uid);
                
                const userDoc = await this.userRef.get();
                this.sessions = userDoc.exists ? userDoc.data().userDeviceSession || {} : {};

                const userProjectIds = userDoc.exists ? Object.keys(userDoc.data().projects) : [];
                this.userProjectIds = userProjectIds;

                const sessionArray = Object.entries(this.sessions)
                    .map(([id, info]) => ({ id, ...info }))
                    .sort((a, b) => new Date(a.loginTime) - new Date(b.loginTime));

                if (sessionArray.length >= 3) {
                    await this.$nextTick();
                    this.$store.dispatch('setLoaderLogin', { type: false });
                    await this.$refs.loginSSODialog.open();
                } else {
                    await this.saveSession();
                    this.$store.dispatch('setLoaderLogin', { type: false });
                    if (this.$route.query.uid && this.$route.query.projectId) {
                        this.$router.replace(`/project/${this.$route.query.projectId}`);
                    } else {
                        const redirect = this.$route.query.redirect || '/dashboard';
                        this.$router.replace(redirect);
                    }
                }
            } catch (error) {
                console.error('Login failed:', error);
                // Josh, 16 Oct QA: a failed Google sign-in left the page spinner on with no message.
                this.$store.dispatch('setLoaderLogin', { type: false });
                this.signInError = signinErrorMessage(error && error.code);
            } finally {
                this.loading = false;
            }
        },
        // `marketingConsented` arrives only from the email register form. The
        // Google path calls this with one argument, so it stays undefined there
        // and nothing is written -- which is what sends those users to the
        // dashboard banner instead.
        //
        // `names` is {firstName, lastName} from the register form or Google, or
        // null when Google has no given_name/family_name. Email sign-up passes it
        // as the third argument (register-form's 'success' event); Google passes
        // null or the pair. undefined = not known (leave the fields alone).
        createUser(user, marketingConsented, names) {
            const now = new Date();
            const newUser = {
                username: user.displayName,
                email: user.email,
                profile_picture: user.photoURL,
                uid: user.uid,
                last_login: now.toISOString(),
                active_pay: false,
                stripe_id: '',
                subscription_level: '',
                subscription_id: '',
                notes: '',
                signInMethod: user.signInMethod,
                projects: {}
            };
            if (names && names.firstName && names.lastName) {
                newUser.firstName = names.firstName;
                newUser.lastName = names.lastName;
            }

            // ⚠ ONLY an explicit tick writes anything. An unticked box is the
            // ABSENCE of an answer, not a refusal -- writing `false` for it
            // would mark them answered and permanently suppress the dashboard
            // banner, losing exactly the people who simply did not notice the
            // checkbox. Absence is what makes the two surfaces compose.
            if (marketingConsented === true) {
                Object.assign(newUser, consentFields(true, 'signup'));
            }

            const userDocRef = db.collection('users').doc(user.uid);
            return db.runTransaction((transaction) => {
                return transaction.get(userDocRef).then((userDoc) => {
                    if (!userDoc.exists) {
                        return transaction.set(userDocRef, newUser);
                    }

                    const existing = {
                        email: user.email,
                        uid: user.uid,
                        signInMethod: user.signInMethod,
                        username: user.displayName,
                        profile_picture: user.photoURL,
                        last_login: now.toISOString()
                    };
                    // This rewrites `username` from the sign-in provider, so the
                    // stored first/last must follow it or go (lib/user-name.js
                    // invariant): Google's pair when it has one, else cleared.
                    if (names !== undefined) {
                        const del = firebase.firestore.FieldValue.delete();
                        existing.firstName = names && names.firstName ? names.firstName : del;
                        existing.lastName = names && names.lastName ? names.lastName : del;
                    }
                    return transaction.update(userDocRef, existing);
                });
            }).then(() => {
                // Transaction successfully committed
                // Removed console.log for SonarQube compliance
            }).catch((error) => {
                console.error('Transaction failed: ', error);
            });
        },
        async saveSession() {
            const sessionArray = Object.entries(this.sessions)
                .map(([id, info]) => ({ id, ...info }))
                .sort((a, b) => new Date(a.loginTime) - new Date(b.loginTime));

            const sessionUpdate = { ...this.sessions };

            if (sessionArray.length >= 3) {
                const oldestSession = sessionArray[0];
                delete sessionUpdate[oldestSession.id];

                await this.userRef.update({
                    [`userDeviceSession.${oldestSession.id}`]: firebase.firestore.FieldValue.delete()
                });
            }

            sessionUpdate[this.sessionID] = this.deviceInfo;

            await this.userRef.update({
                userDeviceSession: sessionUpdate
            });

            await this.userProjectUpdate();

            localStorage.setItem('sessionID', this.sessionID);
            localStorage.setItem('sessionIDDateTime', new Date());
            this.$store.dispatch('setLoaderLogin', { type: false });
            this.$router.replace('/dashboard').then(() => {
                this.loading = false;
            });
        },
        async userProjectUpdate() {
            try {
                if (this.userProjectIds.length > 0) {
                    await Promise.all(this.userProjectIds.map(async (projectId) => {
                        const projectSnapshot = await db.collection('projects').doc(projectId).get();
                        if (projectSnapshot.exists) {
                            const lastChangedDate = projectSnapshot.data().lastChangedDate;
                            if (lastChangedDate) {
                                await this.userRef.update({
                                    [`projects.${projectId}.lastChangedDate`]: lastChangedDate
                                });
                            }
                        }
                    }));
                }
            } catch (error) {
                console.error('Error in userProjectUpdate:', error);
                throw error;
            }
        },
        onDialogClose(type) {
            if (type === 'ok') {
                this.$store.dispatch('setLoaderLogin', { type: true });
                this.saveSession();
            }
        },
        toggleRegister() {
            this.signingIn = !this.signingIn;
        }
    }
};

export default LoginView;
</script>

<style lang="scss" scoped>
.signin-error {
    margin: 12px 0 0;
    color: #d32f2f;
    font-size: 14px;
    line-height: 1.4;
    text-align: center;
}

.login-view {
    min-height: 100vh;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    background-color: #f5f5f5;
    padding: 40px 20px;
}

.login-container {
    width: 100%;
    max-width: 500px;
    margin: 0 auto;
}

.logo-container {
    text-align: center;
    margin-bottom: 24px;
    .logo {
        max-width: 350px;
        height: auto;
        object-fit: contain;
        margin: 0 auto;
        display: block;
    }
}

.login-box {
    background: white;
    padding: 40px;
    border-radius: 8px;
    box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
}

.email-login {
    margin-bottom: 8px;
    .email-login-title {
        display: flex;
        align-items: center;
        justify-content: center;
        margin-bottom: 20px;
        font-size: 24px;
        font-weight: 600;
    }
}

.divider {
    display: flex;
    align-items: center;
    text-align: center;
    margin: 12px 0 16px 0;
    color: #666;
    &::before,
    &::after {
        content: '';
        flex: 1;
        border-bottom: 1px solid #ddd;
    }
    span {
        padding: 0 20px;
        font-size: 14px;
    }
}

.sso-login {
    text-align: center;
    margin-bottom: 0;
    h2 {
        color: #333;
        font-size: 20px;
        margin-bottom: 20px;
    }
    .google-button {
        border-radius: 5px;
        width: 60%;
        height: 48px;
        font-size: 16px;
        text-transform: none;
        background-color: #fff;
        color: #444;
        border: 1px solid #ddd;
        display: flex;
        align-items: center;
        justify-content: center;
        gap: 12px;
        font-weight: 500;
        box-shadow: 0 2px 4px rgba(60,64,67,.08);
        transition: background 0.2s, box-shadow 0.2s;
        margin: 0 auto;
        &:hover {
            background-color: #f5f5f5;
            box-shadow: 0 4px 8px rgba(60,64,67,.12);
        }
        .google-icon {
            width: 24px;
            height: 24px;
            margin-right: 8px;
            background: none;
            box-shadow: none;
        }
    }
}

.terms-container {
    margin-top: 30px;
    text-align: center;
    padding: 0 20px;
    .terms-text {
        color: #666;
        font-size: 12px;
        line-height: 1.5;
        a {
            color: #2196F3;
            text-decoration: none;
            font-weight: 500;
            &:hover {
                text-decoration: underline;
            }
        }
    }
}

@media (max-width: 600px) {
    .login-box {
        padding: 30px 20px;
    }
    .logo-container {
        margin-bottom: 18px;
        .logo {
            max-width: 90px;
        }
    }
}
</style>


// WEBPACK FOOTER //
// login.vue?1c863c06